import { useMemo } from 'react'

/**
 * Confetti firing in from both side edges, tumbling inward and down.
 * A fixed, pointer-transparent overlay: mount it and it plays once;
 * the pieces fade out at the end of their flight. Palette comes from
 * the caller (holiday colors, the winning game's colors).
 *
 * Under prefers-reduced-motion it renders nothing.
 *
 * The keyframes (holidayConfettiL/R) live at the TOP LEVEL of
 * index.css: keyframes inside the @theme block are only emitted when
 * an --animate-* token references them.
 */

interface Piece {
  side: 'l' | 'r'
  top: number
  dx: number
  dy: number
  rot: number
  delay: number
  dur: number
  color: string
  w: number
  h: number
}

function makePieces(palette: string[], count: number): Piece[] {
  const pieces: Piece[] = []
  for (let i = 0; i < count; i++) {
    const side = i % 2 === 0 ? 'l' : 'r'
    pieces.push({
      side,
      top: Math.random() * 45,
      dx: (28 + Math.random() * 34) * (side === 'l' ? 1 : -1),
      dy: 35 + Math.random() * 55,
      rot: (Math.random() * 720 - 360) * 2,
      delay: Math.random() * 0.9,
      dur: 2.4 + Math.random() * 1.8,
      color: palette[i % palette.length],
      w: 6 + Math.random() * 6,
      h: 10 + Math.random() * 8,
    })
  }
  return pieces
}

/** Shift a #rrggbb color toward white (amt > 0) or black (amt < 0). */
export function shade(hex: string, amt: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return hex
  const n = parseInt(m[1], 16)
  const ch = (v: number) => {
    const target = amt >= 0 ? 255 : 0
    const t = Math.abs(amt)
    return Math.round(v + (target - v) * t)
  }
  const r = ch((n >> 16) & 255), g = ch((n >> 8) & 255), b = ch(n & 255)
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`
}

export function ConfettiBurst({ palette, count = 64, zIndex = 120 }: {
  palette: string[]
  count?: number
  zIndex?: number
}) {
  const pieces = useMemo(() => makePieces(palette, count),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [palette.join(','), count])

  const reducedMotion = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  if (reducedMotion || palette.length === 0) return null

  return (
    <div className="fixed inset-0 overflow-hidden" style={{ pointerEvents: 'none', zIndex }} aria-hidden="true">
      {pieces.map((p, i) => (
        <span
          key={i}
          className="holiday-confetti"
          style={{
            [p.side === 'l' ? 'left' : 'right']: '-12px',
            top: `${p.top}vh`,
            width: `${p.w}px`,
            height: `${p.h}px`,
            backgroundColor: p.color,
            animationName: p.side === 'l' ? 'holidayConfettiL' : 'holidayConfettiR',
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.dur}s`,
            ['--dx' as never]: `${p.dx}vw`,
            ['--dy' as never]: `${p.dy}vh`,
            ['--rot' as never]: `${p.rot}deg`,
          } as never}
        />
      ))}
    </div>
  )
}
